import { DRIFT_PPM, PAIR_MAX_ENTER, PAIR_MAX_EXIT } from '../constants.js';
import type { ConnSub } from './connection.js';
import type { IndicatorColour } from './indicator.js';

export type StationRole = 'UAV' | 'FDC' | 'OBSERVER';

/** What a connection last reported in its ping `q` block, as the server holds it (§7.2). */
export interface ConnReport {
  connId: string;
  role: StationRole;
  u: number;
  rttBest: number;
  loss30: number;
  conn: ConnSub;
  state: IndicatorColour;
  hidden: boolean;
  reportAgeMs: number;
  lost: boolean;
}

export interface RoleQuality {
  conn: ConnSub;
  u: number;
  rttBest: number;
  loss30: number;
  state: IndicatorColour;
  hidden: boolean;
  reportAge: number;
  lost: boolean;
}

export interface SyncQuality {
  type: 'sync.quality';
  missionId: string;
  serverTime: number;
  epochId: string;
  pair: { known: boolean; bound: number | null; ok: boolean; weakest: 'UAV' | 'FDC' | null };
  roles: Partial<Record<StationRole, RoleQuality>>;
  clients: Array<{ connId: string; role: StationRole; u: number; conn: ConnSub; state: IndicatorColour; hidden: boolean; lost: boolean }>;
}

const REPORT_MAX_AGE_MS = 10_000;

function effectiveU(r: ConnReport): number {
  return r.u + DRIFT_PPM * 1e-6 * r.reportAgeMs;
}

/** Worst connection of a role: the weakest screen is the one that bounds the pair (§7.2). */
function roleQuality(reports: ConnReport[]): { q: RoleQuality; u: number } | null {
  const live = reports.filter((r) => r.reportAgeMs <= REPORT_MAX_AGE_MS && !r.lost);
  if (live.length === 0) {
    // still describe the role for the stations strip, marked lost, if we have anything
    const any = reports.sort((a, b) => a.reportAgeMs - b.reportAgeMs)[0];
    if (!any) return null;
    return { q: { conn: 'LOST', u: any.u, rttBest: any.rttBest, loss30: any.loss30, state: 'RED', hidden: any.hidden, reportAge: any.reportAgeMs, lost: true }, u: Number.POSITIVE_INFINITY };
  }
  const worst = live.reduce((a, r) => (effectiveU(r) > effectiveU(a) ? r : a));
  return {
    q: { conn: worst.conn, u: worst.u, rttBest: worst.rttBest, loss30: worst.loss30, state: worst.state, hidden: worst.hidden, reportAge: worst.reportAgeMs, lost: false },
    u: effectiveU(worst),
  };
}

/**
 * Server-side pair computation, per mission, every second and on change. `prevOk` carries the
 * hysteresis (enter ≥ 1000 → false, exit ≤ 900 → true) so that both clients flip together.
 */
export function computeSyncQuality(
  reports: ConnReport[],
  prevOk: boolean,
  meta: { missionId: string; serverTime: number; epochId: string },
): SyncQuality {
  const byRole: Record<StationRole, ConnReport[]> = { UAV: [], FDC: [], OBSERVER: [] };
  for (const r of reports) byRole[r.role].push(r);
  const fdc = roleQuality(byRole.FDC);
  const uav = roleQuality(byRole.UAV);
  const obs = roleQuality(byRole.OBSERVER);

  const known = !!fdc && !!uav && Number.isFinite(fdc.u) && Number.isFinite(uav.u);
  let bound: number | null = null;
  let ok = false;
  let weakest: 'UAV' | 'FDC' | null = null;
  if (known && fdc && uav) {
    bound = Math.round(fdc.u + uav.u);
    ok = prevOk ? bound < PAIR_MAX_ENTER : bound <= PAIR_MAX_EXIT;
    weakest = uav.u >= fdc.u ? 'UAV' : 'FDC';
  }

  const roles: SyncQuality['roles'] = {};
  if (fdc) roles.FDC = fdc.q;
  if (uav) roles.UAV = uav.q;
  if (obs) roles.OBSERVER = obs.q;

  return {
    type: 'sync.quality',
    ...meta,
    pair: { known, bound, ok, weakest },
    roles,
    clients: reports.map((r) => ({ connId: r.connId, role: r.role, u: r.u, conn: r.conn, state: r.state, hidden: r.hidden, lost: r.lost })),
  };
}

/** Per-recipient filtering (C-4): UAV and FDC never learn an Observer's link state. */
export function filterSyncQualityFor(msg: SyncQuality, recipient: 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN'): SyncQuality {
  if (recipient === 'OBSERVER' || recipient === 'ADMIN') return msg;
  const roles = { ...msg.roles };
  delete roles.OBSERVER;
  return { ...msg, roles, clients: msg.clients.filter((c) => c.role !== 'OBSERVER') };
}

export type StationState = 'LIVE' | 'INTERMITTENT' | 'STALE' | 'LOST';

/** The stations strip pill for another role (§7.3). */
export function stationState(q: RoleQuality | undefined, staleAfterMs = 5_000): { state: StationState; detail: string } {
  if (!q || q.lost) return { state: 'LOST', detail: q ? `last ${Math.floor(q.reportAge / 1000)}s` : '' };
  if (q.reportAge > staleAfterMs) return { state: 'STALE', detail: `${Math.floor(q.reportAge / 1000)}s` };
  if (q.conn === 'INTERMITTENT') return { state: 'INTERMITTENT', detail: `±${(Math.ceil(q.u / 100) / 10).toFixed(1)}s` };
  return { state: 'LIVE', detail: `±${(Math.ceil(q.u / 100) / 10).toFixed(1)}s` };
}
